/* Creating a workspace — STORY-004.
 *
 * The first request a User makes that is not about their own identity. It is
 * authenticated but pre-tenancy: the User has zero Memberships at this point,
 * so there is no Organisation to scope it to and nothing tenant-scoped may be
 * touched while serving it.
 */
import * as z from 'zod';
import { membershipSchema } from './auth.js';
/** Long enough to be a real name, short enough to render in a nav bar. */
export const ORGANISATION_NAME_MAX = 60;
export const createOrganisationRequestSchema = z.object({
    /* Trimmed before the length is judged, so "   " is empty rather than three
     * characters, and a name that only differs by padding is the same name. */
    name: z.string().trim().min(1).max(ORGANISATION_NAME_MAX),
    /* Three uppercase letters here, and nothing more: the real check is that the
     * runtime knows the code, which `parseCurrency` in packages/domain does
     * against `Intl.supportedValuesOf('currency')`. `i18n.md` forbids a committed
     * list of "the currencies we support" — it is stale the day somebody signs up
     * outside it, and it duplicates data the platform already has.
     *
     * Chosen at creation and effectively permanent: it cannot be changed once
     * Transactions exist, and it is the one setting that cannot be detected
     * reliably. Timezone and locale are detected rather than asked, because both
     * are editable afterwards and neither is destructive to get briefly wrong. */
    currency: z
        .string()
        .length(3)
        .regex(/^[A-Z]{3}$/, 'A three-letter ISO 4217 code, uppercase.'),
    /* Detected from the device, sent for the record, and shown for confirmation
     * only — never presented as a question. An Event takes its timezone from its
     * Venue, so this is a fallback rather than a decision. */
    timezone: z.string().min(1),
});
export const createOrganisationResponseSchema = z.object({
    /* The Membership the caller now holds, in the same shape sign-in returns, so
     * the client updates its cached User with a value it already knows how to
     * read rather than a second shape meaning the same thing. */
    membership: membershipSchema,
});
/**
 * One workspace, as the Member who belongs to it sees it.
 *
 * Read back rather than remembered: a Membership carries only the id, the name,
 * and the role, so a screen fed by the create response alone shows nothing to
 * somebody who reloaded or arrived from a bookmark.
 *
 * `plan` is a plain string rather than a union of the known values on purpose.
 * The client renders what it is told, and pinning the names here would mean a
 * new plan breaks the parse in an already-shipped mobile client — which
 * `mobile-expo.md` warns keeps calling indefinitely, because store review means
 * the installed version always lags.
 */
export const organisationSummarySchema = z.object({
    organisationId: z.string(),
    name: z.string(),
    role: z.string(),
    plan: z.string(),
    currency: z.string().length(3),
    /** An IANA identifier, never an offset. */
    timezone: z.string(),
    /** Months, or `null` for off — STORY-043. Off is the default and stays it. */
    personRetentionMonths: z.number().int().nullable(),
});
/** Renaming the workspace — one of the eight Owner-only actions (ADR-0011). */
export const renameOrganisationRequestSchema = z.object({
    name: z.string().trim().min(1).max(ORGANISATION_NAME_MAX),
});
/**
 * How long a Person with no activity is kept — STORY-043.
 *
 * A fixed ladder rather than any number of months, and the reason is the
 * direction of the mistake: the setting only ever destroys data, so a typo of
 * `1` erases a whole contact book on the next sweep. Three options cover the
 * range anybody has a view about, and `design.md` prefers a real `<select>`
 * over a number field for exactly this kind of value.
 *
 * Mirrored as a CHECK constraint on `organisations`. That is deliberate rather
 * than duplication: `database-postgres.md` puts a rule in the database when it
 * can be expressed there, because a rule enforced only in application code is
 * bypassed by the first hand-written statement.
 */
export const PERSON_RETENTION_MONTHS = [12, 24, 36];
/**
 * `null` switches erasure off, and is the only way back to off.
 *
 * Nullable rather than a separate enabled flag: two fields can contradict each
 * other and one cannot, and "off" is genuinely the absence of a period rather
 * than a period with a flag beside it.
 */
export const setPersonRetentionRequestSchema = z.object({
    months: z.union([z.literal(12), z.literal(24), z.literal(36), z.null()]),
});
/**
 * One row of the team roster.
 *
 * The Role travels with each Member because it is per Membership — the same
 * User may be an Owner here and a Member in the next workspace, and a client
 * that assumed otherwise would show the wrong thing on the switch.
 */
export const memberSchema = z.object({
    userId: z.string(),
    displayName: z.string(),
    email: z.string(),
    role: z.string(),
    joinedAt: z.string(),
});
export const membersResponseSchema = z.object({
    members: z.array(memberSchema),
});
/** Promoting or demoting — Owner-only, and guarded so the last Owner stays. */
export const changeMemberRoleRequestSchema = z.object({
    role: z.enum(['owner', 'member']),
});
//# sourceMappingURL=organisations.js.map